import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { PiApi } from '@/api';
import { formatMoneyWithSymbol, formatDate, formatDateTime } from '@/lib/format';
import {
  ChevronLeft, ChevronDown, Download, Send, FileText, Mail, MessageSquare, Check, X, Trash2,
  Building2, CalendarDays, Clock, UserRound, Landmark, MapPin, Truck, Hash,
} from 'lucide-react';

function SectionHeader({ title, expanded, onToggle }: { title: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      className="card-pad w-full text-left flex items-center justify-between hover:bg-slate-50"
      onClick={onToggle}
    >
      <h3 className="font-semibold">{title}</h3>
      <ChevronDown size={18} className={`text-muted transition-transform ${expanded ? 'rotate-180' : ''}`} />
    </button>
  );
}

function InfoField({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 text-muted shrink-0">{icon}</div>
      <div>
        <div className="text-xs text-muted font-medium">{label}</div>
        <div className="font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}

/** Splits a frozen description_snapshot ("Name - spec1\nspec2\n...") into a bold
 * heading plus a list of spec chips — mirrors how render.ts splits it for the PDF. */
function parseLineDescription(snapshot: string): { name: string; specs: string[] } {
  const idx = snapshot.indexOf(' - ');
  if (idx === -1) return { name: snapshot, specs: [] };
  return {
    name: snapshot.slice(0, idx),
    specs: snapshot.slice(idx + 3).split('\n').filter(Boolean),
  };
}

export default function PiDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState('');
  // Send channel picker
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    lines: true,
    terms: true,
    approvals: false,
    comms: false,
    versions: false,
  });

  const piQuery = useQuery({
    queryKey: ['pi', id],
    queryFn: () => PiApi.get(id!),
  });

  const pi = piQuery.data;

  const markAcceptedMutation = useMutation({
    mutationFn: () => PiApi.markAccepted(id!),
    onSuccess: () => {
      push({ tone: 'success', title: 'PI marked as accepted' });
      queryClient.invalidateQueries({ queryKey: ['pi', id] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed', body: err.message });
    },
  });

  const markLostMutation = useMutation({
    mutationFn: () => PiApi.markLost(id!, lostReason),
    onSuccess: () => {
      push({ tone: 'success', title: 'PI marked as lost' });
      setShowLostModal(false);
      queryClient.invalidateQueries({ queryKey: ['pi', id] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed', body: err.message });
    },
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => PiApi.delete(id!),
    onSuccess: () => {
      push({ tone: 'success', title: 'PI deleted' });
      navigate('/pis');
    },
    onError: (err: any) => push({ tone: 'error', title: 'Failed to delete PI', body: err.message }),
  });

  const openSendModal = () => {
    setSendEmail(true);
    setSendWhatsapp(true);
    setEmailTo(pi?.customer_email || '');
    setEmailCc('');
    setEmailSubject(`Proforma Invoice ${pi?.pi_number ?? ''}`);
    setEmailBody('');
    setShowSendModal(true);
  };

  const sendMutation = useMutation({
    mutationFn: async () => {
      const ccList = emailCc.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
      const results: string[] = [];
      if (sendEmail) {
        await PiApi.sendEmail(id!, {
          to: emailTo,
          cc: ccList.length > 0 ? ccList : undefined,
          subject: emailSubject,
          body: emailBody || undefined,
        });
        results.push('Email sent');
      }
      if (sendWhatsapp) {
        await PiApi.sendWhatsApp(id!);
        results.push('WhatsApp sent');
      }
      return results;
    },
    onSuccess: (results) => {
      push({ tone: 'success', title: 'Sent', body: results.join(' • ') });
      setShowSendModal(false);
      queryClient.invalidateQueries({ queryKey: ['pi', id] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed', body: err.message });
    },
  });

  /**
   * Download mode:
   *   - 'view'     → opens the PDF inline in a new tab (good for quick preview)
   *   - 'download' → forces a file save with a sensible filename
   */
  const pdfMutation = useMutation({
    mutationFn: async (mode: 'view' | 'download') => {
      const blob = await PiApi.pdf(id!);
      return { blob, mode };
    },
    onSuccess: ({ blob, mode }) => {
      const url = URL.createObjectURL(blob);
      const safeCustomer = (pi?.customer_name ?? 'customer')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .slice(0, 24);
      const safePiNumber = (pi?.pi_number ?? id ?? '').replace(/[/\\]/g, '-');
      const filename = `RDX_PI_${safePiNumber}${
        pi && pi.revision > 0 ? `_R${pi.revision}` : ''
      }_${safeCustomer}.pdf`;

      if (mode === 'view') {
        window.open(url, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke after a delay so the new tab/dialog has time to read the blob.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to fetch PDF', body: err.message });
    },
  });

  if (piQuery.isLoading) {
    return <div className="text-center py-12 text-muted">Loading…</div>;
  }

  if (!pi) {
    return <div className="text-center py-12 text-muted">PI not found</div>;
  }

  // Admin can act on any PI. A manager can act only on PIs they created
  // themselves — full self-service on their own PI, no visibility or actions
  // on anyone else's. Every action here is available in every status —
  // negotiation commonly means re-editing or re-sending a PI that's already
  // been sent, or even one already marked accepted/lost. Delete stays
  // admin-only and draft-only (the one exception — deleting a sent/closed PI
  // destroys real history, unlike editing it).
  const isOwnPi = user?.role === 'admin' || user?.id === pi.created_by;
  const canEdit = isOwnPi;
  const canDelete = pi.status === 'draft' && user?.role === 'admin';
  const canSend = isOwnPi;
  const canAccept = isOwnPi;
  const canMarkLost = isOwnPi;

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((s) => ({ ...s, [section]: !s[section] }));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button className="btn btn-ghost" onClick={() => navigate('/pis')}>
            <ChevronLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold">{pi.pi_number}</h1>
            <p className="text-sm text-muted mt-1">Revision {pi.revision}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={pi.status} />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="card card-pad flex flex-wrap gap-2">
        {canEdit && (
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/pis/${id}/edit`)}
          >
            Edit
          </button>
        )}
        {canSend && (
          <button
            className="btn btn-primary gap-1"
            onClick={() => openSendModal()}
          >
            <Send size={16} /> Send
          </button>
        )}
        {/* PDF actions — always available; users can preview or save at any status. */}
        <button
          className="btn btn-secondary gap-1"
          onClick={() => pdfMutation.mutate('view')}
          disabled={pdfMutation.isPending}
          title="Open the PDF in a new tab"
        >
          <FileText size={16} /> {pdfMutation.isPending ? 'Generating PDF…' : 'View PDF'}
        </button>
        <button
          className="btn btn-secondary gap-1"
          onClick={() => pdfMutation.mutate('download')}
          disabled={pdfMutation.isPending}
          title="Save the PDF to your computer"
        >
          <Download size={16} /> {pdfMutation.isPending ? 'Generating PDF…' : 'Download'}
        </button>
        {canAccept && (
          <button
            className="btn btn-primary gap-1"
            onClick={() => markAcceptedMutation.mutate()}
            disabled={markAcceptedMutation.isPending}
          >
            <Check size={16} /> Mark Accepted
          </button>
        )}
        {canMarkLost && (
          <button
            className="btn btn-danger gap-1"
            onClick={() => setShowLostModal(true)}
          >
            <X size={16} /> Mark Lost
          </button>
        )}
        {canDelete && (
          <button
            className="btn btn-danger gap-1"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={16} /> Delete
          </button>
        )}
      </div>

      {/* Header Section */}
      <div className="card overflow-hidden">
        <SectionHeader title="Header Information" expanded={expandedSections.header} onToggle={() => toggleSection('header')} />
        {expandedSections.header && (
          <div className="card-pad pt-5 space-y-5 border-t border-line">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 text-sm">
              <InfoField icon={<Building2 size={16} />} label="Customer" value={pi.customer_name} />
              <InfoField icon={<UserRound size={16} />} label="Customer Type" value={<span className="capitalize">{pi.customer_type}</span>} />
              {pi.buyer_ref_no && (
                <InfoField icon={<Hash size={16} />} label="Buyer's Ref./Order No." value={pi.buyer_ref_no} />
              )}
              <InfoField icon={<Landmark size={16} />} label="Currency" value={pi.currency} />
              <InfoField icon={<CalendarDays size={16} />} label="PI Date" value={formatDate(pi.pi_date)} />
              <InfoField icon={<Clock size={16} />} label="Validity" value={`${pi.validity_days} days`} />
              <InfoField icon={<UserRound size={16} />} label="Created By" value={pi.created_by_name || pi.created_by} />
              <InfoField icon={<CalendarDays size={16} />} label="Created At" value={formatDateTime(pi.created_at)} />
              {pi.submitted_at && (
                <InfoField icon={<CalendarDays size={16} />} label="Submitted At" value={formatDateTime(pi.submitted_at)} />
              )}
            </div>

            {(pi.billing_address || pi.shipping_address) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-line">
                {pi.billing_address && (
                  <div className="pt-4">
                    <div className="flex items-center gap-2 text-xs text-muted font-medium">
                      <MapPin size={14} /> Billing Address
                    </div>
                    <div className="text-sm mt-1.5 whitespace-pre-line">{pi.billing_address}</div>
                  </div>
                )}
                <div className="pt-4">
                  <div className="flex items-center gap-2 text-xs text-muted font-medium">
                    <Truck size={14} /> Shipping Address
                  </div>
                  {!pi.shipping_address || pi.shipping_address === pi.billing_address ? (
                    <div className="mt-1.5">
                      <span className="badge bg-slate-100 text-slate-600">Same as billing</span>
                    </div>
                  ) : (
                    <div className="text-sm mt-1.5 whitespace-pre-line">{pi.shipping_address}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lines Section */}
      <div className="card overflow-hidden">
        <SectionHeader title="Line Items" expanded={expandedSections.lines} onToggle={() => toggleSection('lines')} />
        {expandedSections.lines && (
          <div className="p-0 border-t border-line overflow-x-auto">
            <table className="table-clean w-full">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Discount</th>
                  <th>Subtotal</th>
                  <th>CGST</th>
                  <th>SGST</th>
                  <th>IGST</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(pi.lines ?? []).map((line, idx) => {
                  const { name, specs } = parseLineDescription(line.description_snapshot ?? '');
                  return (
                    <tr key={idx}>
                      <td className="text-sm">
                        <div className="font-medium">{name}</div>
                        {specs.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {specs.map((s, i) => (
                              <span key={i} className="badge bg-slate-100 text-slate-600 font-normal">{s}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>{line.quantity}</td>
                      <td>{formatMoneyWithSymbol(line.unit_price, pi.currency)}</td>
                      <td>
                        {line.discount_pct > 0 && `${line.discount_pct}%`}
                        {line.discount_amount > 0 && formatMoneyWithSymbol(line.discount_amount, pi.currency)}
                      </td>
                      <td>{formatMoneyWithSymbol(line.line_subtotal, pi.currency)}</td>
                      <td>{formatMoneyWithSymbol(line.cgst_amount, pi.currency)}</td>
                      <td>{formatMoneyWithSymbol(line.sgst_amount, pi.currency)}</td>
                      <td>{formatMoneyWithSymbol(line.igst_amount, pi.currency)}</td>
                      <td className="font-medium">{formatMoneyWithSymbol(line.line_total, pi.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="card overflow-hidden">
        <div className="card-pad grid grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-muted font-medium">Subtotal</div>
            <div className="text-lg font-semibold mt-1.5">{formatMoneyWithSymbol(pi.subtotal, pi.currency)}</div>
          </div>
          <div>
            <div className="text-xs text-muted font-medium">Discount</div>
            <div className="text-lg font-semibold mt-1.5">
              {Number(pi.discount_total) > 0 ? `− ${formatMoneyWithSymbol(pi.discount_total, pi.currency)}` : formatMoneyWithSymbol(0, pi.currency)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted font-medium">Tax</div>
            <div className="text-lg font-semibold mt-1.5">{formatMoneyWithSymbol(pi.tax_total, pi.currency)}</div>
          </div>
        </div>
        <div className="card-pad flex items-center justify-between bg-rdx-50 border-t border-line">
          <div className="text-sm font-medium text-rdx-700">Grand Total</div>
          <div className="text-2xl font-bold text-rdx">{formatMoneyWithSymbol(pi.grand_total, pi.currency)}</div>
        </div>
      </div>

      {/* Terms Section */}
      <div className="card overflow-hidden">
        <SectionHeader title="Terms & Conditions" expanded={expandedSections.terms} onToggle={() => toggleSection('terms')} />
        {expandedSections.terms && (
          <div className="card-pad pt-5 space-y-3 border-t border-line text-sm">
            {pi.payment_preset && (
              <div>
                <div className="font-medium mb-1">Payment Terms: {pi.payment_preset.name}</div>
                <div className="text-muted ml-2">Mode: {pi.payment_preset.payment_mode}</div>
              </div>
            )}
            {(pi.warranty_preset || pi.warranty_months_override != null) && (
              <div>
                <div className="font-medium mb-1">Warranty{pi.warranty_preset ? `: ${pi.warranty_preset.name}` : ' (custom)'}</div>
                <div className="text-muted ml-2">
                  {pi.warranty_months_override ?? pi.warranty_preset?.machine_months} months on machine
                  {pi.warranty_preset && pi.warranty_months_override != null && pi.warranty_months_override !== pi.warranty_preset.machine_months && (
                    <span className="text-xs"> (overridden from preset default of {pi.warranty_preset.machine_months})</span>
                  )}
                </div>
              </div>
            )}
            <div>
              <div className="font-medium mb-1">Lead Time</div>
              <div className="text-muted ml-2">{pi.lead_time_weeks} weeks</div>
            </div>
            <div>
              <div className="font-medium mb-1">Quote Validity</div>
              <div className="text-muted ml-2">{pi.validity_days} days</div>
            </div>
            {pi.advance_pct != null && (
              <div>
                <div className="font-medium mb-1">Advance</div>
                <div className="text-muted ml-2">{Number(pi.advance_pct)}%</div>
              </div>
            )}
            {pi.notes && (
              <div>
                <div className="font-medium mb-1">Notes</div>
                <div className="text-muted ml-2 whitespace-pre-line">{pi.notes}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Approvals */}
      {pi.approvals && pi.approvals.length > 0 && (
        <div className="card overflow-hidden">
          <SectionHeader title="Approvals" expanded={expandedSections.approvals} onToggle={() => toggleSection('approvals')} />
          {expandedSections.approvals && (
            <div className="p-0 border-t border-line overflow-x-auto">
              <table className="table-clean w-full">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Reason</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pi.approvals.map((a) => (
                    <tr key={a.id}>
                      <td className="font-medium capitalize">{a.action}</td>
                      <td>{a.actor_name}</td>
                      <td className="text-sm text-muted">{a.reason || '-'}</td>
                      <td className="text-sm text-muted">{formatDateTime(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Communications */}
      {pi.comms && pi.comms.length > 0 && (
        <div className="card overflow-hidden">
          <SectionHeader title="Communications" expanded={expandedSections.comms} onToggle={() => toggleSection('comms')} />
          {expandedSections.comms && (
            <div className="p-0 border-t border-line overflow-x-auto">
              <table className="table-clean w-full">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Recipient</th>
                    <th>Status</th>
                    <th>Sent By</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pi.comms.map((c) => (
                    <tr key={c.id}>
                      <td className="font-medium capitalize">{c.channel}</td>
                      <td className="text-sm">{c.recipient}</td>
                      <td className="text-sm capitalize">{c.status}</td>
                      <td className="text-sm text-muted">{c.triggered_by_name}</td>
                      <td className="text-sm text-muted">{formatDateTime(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Versions */}
      {pi.versions && pi.versions.length > 0 && (
        <div className="card overflow-hidden">
          <SectionHeader title="Versions" expanded={expandedSections.versions} onToggle={() => toggleSection('versions')} />
          {expandedSections.versions && (
            <div className="p-0 border-t border-line">
              <div className="space-y-2">
                {pi.versions.map((v) => (
                  <button
                    key={v.id}
                    className="block w-full text-left px-5 py-3 hover:bg-slate-50 border-b border-line last:border-0"
                    onClick={() => navigate(`/pis/${v.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Revision {v.revision}</div>
                        <div className="text-xs text-muted mt-0.5">
                          {v.sent_at
                            ? `Sent ${formatDateTime(v.sent_at)}`
                            : (v as any).created_at
                              ? `Created ${formatDateTime((v as any).created_at)}`
                              : '-'}
                        </div>
                      </div>
                      <StatusBadge status={v.status} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <Modal
        open={showLostModal}
        onClose={() => setShowLostModal(false)}
        title="Mark as Lost"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowLostModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={() => markLostMutation.mutate()}
              disabled={markLostMutation.isPending}
            >
              Mark Lost
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="label">Reason (Optional)</label>
            <textarea
              className="input mt-1"
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card card-pad w-[360px] space-y-4">
            <h3 className="text-sm font-semibold text-ink">Delete draft PI?</h3>
            <p className="text-xs text-muted">This will permanently delete <span className="font-medium text-ink">{pi.pi_number}</span> and all its line items. This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button className="btn btn-sm btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send PI"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowSendModal(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary gap-1"
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending || (!sendEmail && !sendWhatsapp) || (sendEmail && !emailTo)}
            >
              <Send size={14} />
              {sendMutation.isPending ? 'Sending…' : 'Send'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Channel checkboxes */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              <Mail size={16} className="text-muted" />
              <span className="text-sm font-medium">Email</span>
              {pi.customer_email && (
                <span className="text-xs text-muted">{pi.customer_email}</span>
              )}
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={sendWhatsapp}
                onChange={(e) => setSendWhatsapp(e.target.checked)}
              />
              <MessageSquare size={16} className="text-muted" />
              <span className="text-sm font-medium">WhatsApp</span>
              {pi.customer_whatsapp && (
                <span className="text-xs text-muted">{pi.customer_whatsapp}</span>
              )}
            </label>
          </div>

          {/* Email fields — shown only when email checked */}
          {sendEmail && (
            <div className="space-y-3 border-t border-line pt-3">
              <div>
                <label className="label">To *</label>
                <input
                  type="email"
                  className="input mt-1"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                />
              </div>
              <div>
                <label className="label">CC</label>
                <input
                  type="text"
                  className="input mt-1"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  placeholder="Comma or semicolon separated"
                />
              </div>
              <div>
                <label className="label">Subject</label>
                <input
                  type="text"
                  className="input mt-1"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Body <span className="text-muted text-xs">(leave blank for default)</span></label>
                <textarea
                  className="input mt-1"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={5}
                  placeholder="Leave blank to use the default email template"
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
